import { useEffect } from "react";
import { config } from "../config";
import { buildspecTemplate, databaseName, months } from "./constants";
import { Log, Resource, ResourceFile, ResourceFolder, User } from "./interfaces";

export const randString = (len: number) => Array.from({ length: len }, () => Math.random().toString(36).charAt(2)).join('');

export const getPrettyDate = (date: string) => {
  const d = new Date(date);
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

// get path of folder from id
export const getPathFromId = (root: ResourceFolder, id: string) => {
  if (id.length === 0) return '';
  let path = '/';
  const searchPath = (folder: ResourceFolder): string => {
    for (const item of folder.items) {
      if (item.id === id) return item.name;
      if ((item as ResourceFolder).items) {
        const foundPath = searchPath(item as ResourceFolder);
        if (foundPath) return item.name + '/' + foundPath;
      }
    }
    return '';
  }
  return path + searchPath(root);
}

// check if file exist
export const fileExist = (id: string, folder: ResourceFolder) => {
  for (const item of folder.items) {
    if (item.id === id) return true;
  }
  let existInChildFolder = false;
  for (const item of folder.items) {
    if ((item as ResourceFolder).items) { // if it's folder
      existInChildFolder = fileExist(id, item as ResourceFolder);
      if (existInChildFolder) return true;
    }
  }
  return false;
}

// find file from root
export const findFile = (id: string, root: ResourceFolder): ResourceFile | undefined => {
  for (const item of root.items) {
    if (item.id === id) return item;
  }
  let file;
  for (const item of root.items) {
    if ((item as ResourceFolder).items) { // if it's folder
      file = findFile(id, item as ResourceFolder);
      if (file) return file;
    }
  }
}

// deep copy
export const copy = (object: any) => JSON.parse(JSON.stringify(object));

// upload file to s3 bucket
export const uploadToBucket = async (path: string, content: string) => {
  await fetch(`${config.api.s3}`, {
    method: 'POST',
    body: JSON.stringify({
      bucket: `${databaseName.replace('_', '-')}-bucket`,
      path: path,
      content: content,
    }),
  });
}

// get file content from s3 bucket
export const getContentFromBucket = async (path: string) => {
  try {
    const res = await (await fetch(`${config.api.s3}?bucket=${databaseName.replace('_', '-')}-bucket&path=${path}`)).json();
    return res;
  } catch (error) {
    console.log(error);
  }
  return '';
}

// delete from content in s3 bucket
export const deleteFromBucket = async (path: string, isFolder?: boolean) => {
  try {
    await fetch(`${config.api.s3}?bucket=${databaseName.replace('_', '-')}-bucket&path=${path}&is_folder=${isFolder ? 'True' : 'False'}`, { method: 'DELETE' } )
  } catch (error) {
    console.log(error)
  }
}

// setup buildspec file for codebuild
export const setupBuildSpec = (user: User, resource: Resource) => {
  // create files in code build environment
  let filesCommands = buildspecTemplate.filesCommands.replaceAll('<USERNAME>', user.username).replaceAll('<RESOURCE_ID>', resource.id);
  // copy other files
  const addFile = (folder: ResourceFolder) => {
    for (const item of folder.items) {
      if ((item as ResourceFolder).items) {
        addFile(item as ResourceFolder);
      } else {
        filesCommands += `
      - aws s3 cp s3://${databaseName.replace('_', '-')}-bucket/${user.username}/${resource.id}/${item.id} ./${item.name}
        `;
      }
    }
  }
  addFile(resource.root);

  // build commands
  let buildCommands = buildspecTemplate.buildCommands.replaceAll('<PLUGIN_NAME>', resource.mainClass);
  // store jar file to s3
  let storeCommands = buildspecTemplate.storeCommands.replaceAll('<PLUGIN_NAME>', resource.mainClass).replaceAll('<USERNAME>', user.username).replaceAll('<RESOURCE_ID>', resource.id);

  return buildspecTemplate.head + filesCommands + buildCommands + storeCommands;
}

// get pretty logs
export const getPrettyLogs = (logs: string[]): Log[] => {
  let prettyLogs: Log[] = [];
  // uploading file logs
  prettyLogs = [ ...prettyLogs, { message: 'Uploading files...', fontStyle: 'bold' }, { message: '\n' } ];
  // building logs
  const buildCommandIndex = logs.findIndex(log => log.includes('Running command javac -d classes -cp ./spigot-api.jar *.java'));
  if (buildCommandIndex !== -1) prettyLogs = [ ...prettyLogs, { message: 'Building...', fontStyle: 'bold' }, { message: '\n' } ];
  logs = logs.slice(buildCommandIndex + 1);
  // return if logs after building is empty
  if (logs.length === 0) return prettyLogs;

  // add error messages
  if (!logs.filter(l => l.trim().length > 0)[0].startsWith('[Container]')) {
    logs = logs.slice(0, logs.findIndex(log => log.startsWith('[Container]')));
    prettyLogs = [ ...prettyLogs, { message: 'Error occurred while building', color: '#ff4d54', fontStyle: 'bold' }, ...logs.map(log => ({ message: log })) ];
  }
  // add success message
  if (logs.find(log => log.includes('POST_BUILD State: SUCCEEDED'))) {
    prettyLogs = [ ...prettyLogs, { message: 'Build status: SUCCESS', fontStyle: 'bold' }, { message: '\n' } ];
  } else if (logs.find(log => log.includes('POST_BUILD State: FAILED'))) {
    prettyLogs = [ ...prettyLogs, { message: 'Build status: FAILED', fontStyle: 'bold' }, { message: '\n' } ];
  }

  return prettyLogs;
}

// save the file to local
export const saveBufferAsFile = (buffer: ArrayBuffer, filePath: string, contentType: string): void => {
  // Convert the ArrayBuffer to a Blob
  const blob = new Blob([new Uint8Array(buffer)], { type: contentType });
  // Create a Blob URL
  const blobUrl = window.URL.createObjectURL(blob);
  // Create a download link and trigger the download
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filePath;
  // Programmatically click the link to trigger the download
  a.click();

  // Revoke the Blob URL to release resources
  window.URL.revokeObjectURL(blobUrl);
}

export const useUnSavedChangesWarning = (condition: boolean) => {
  useEffect(() => {
    const beforeunloadHandler = (e: BeforeUnloadEvent) => {
      if (condition) {
        e.preventDefault();
        return '';
      }
    }
    window.addEventListener('beforeunload', beforeunloadHandler);
    return () => window.removeEventListener('beforeunload', beforeunloadHandler);
  }, [condition]);
}